import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Skeleton, Tooltip as MuiTooltip } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { bloomberg } from '../theme/bloombergTheme';
import MacroSparkline from './MacroSparkline';
import { getMacroOverview, MacroOverviewSeries } from '../services/api';
import {
  formatMacroValue,
  formatMacroChange,
  changeColor,
  changeDirection,
} from '../utils/macroFormat';

const MONO = bloomberg.font.mono;

// The six that answer "what regime is this?" — curve shape, credit stress,
// financial conditions, volatility, labour, manufacturing. Short labels because
// the tile is ~150px wide; the full names live on the Macro page.
const TILES: { id: string; label: string }[] = [
  { id: 't10y3m', label: 'YIELD CURVE' },
  { id: 'hy-oas', label: 'HY SPREAD' },
  { id: 'nfci', label: 'FIN COND' },
  { id: 'vix', label: 'VIX' },
  { id: 'claims', label: 'JOBLESS' },
  { id: 'ism-mfg-est', label: 'MFG PMI' },
];

const HORIZON = '1m' as const;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Compact macro regime row for the dashboard.
 *
 * The full macro view is a page you visit deliberately; this is the glance that
 * doesn't require deciding to go look. One request paints all six tiles.
 */
const MacroRegimeStrip: React.FC = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState<MacroOverviewSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getMacroOverview({ ids: TILES.map(t => t.id) });
      setSeries(res.series);
      setFailed(false);
    } catch (err) {
      console.error('Error fetching macro regime strip:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Same refresh discipline as the macro page: a dashboard left open on a
  // second monitor should not show yesterday's regime indefinitely.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const timer = window.setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.clearInterval(timer);
    };
  }, [load]);

  // Preserve the configured order — the API returns catalogue order, not ours.
  const ordered = TILES
    .map(t => ({ tile: t, s: series.find(x => x.id === t.id) }))
    .filter((x): x is { tile: typeof TILES[number]; s: MacroOverviewSeries } => !!x.s);

  if (failed && !ordered.length) return null;   // never replace the dashboard with an error

  return (
    <Box
      onClick={() => navigate('/macro-overview')}
      sx={{
        mb: 3, px: 1.5, py: 1, cursor: 'pointer',
        border: `1px solid ${bloomberg.border.primary}`,
        borderRadius: 1,
        bgcolor: bloomberg.bg.secondary,
        display: 'flex', alignItems: 'center', gap: 1.5,
        overflowX: 'auto',
        '&:hover': { borderColor: bloomberg.border.secondary },
      }}
    >
      <Typography
        sx={{
          fontFamily: MONO, fontSize: bloomberg.fontSize.micro, fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
          color: bloomberg.accent.orange, whiteSpace: 'nowrap', flexShrink: 0,
        }}
      >
        Macro Regime
      </Typography>

      {loading && !ordered.length
        ? [...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={30} sx={{ flex: 1, minWidth: 110 }} />
          ))
        : ordered.map(({ tile, s }) => {
            const change = s.latest?.changes?.[HORIZON];
            const dir = changeDirection(change);
            const color = changeColor(change, s.good_when);
            return (
              <MuiTooltip
                key={s.id}
                title={`${s.name}${s.latest ? ` · ${s.latest.date}` : ''}`}
                arrow
                enterDelay={400}
              >
                <Box sx={{ flex: 1, minWidth: 110, flexShrink: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: MONO, fontSize: bloomberg.fontSize.micro,
                      color: bloomberg.text.muted, letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tile.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: MONO, fontSize: bloomberg.fontSize.bodyDense,
                        fontWeight: 600, color: bloomberg.text.primary, whiteSpace: 'nowrap',
                      }}
                    >
                      {formatMacroValue(s.latest?.value, s.unit)}
                    </Typography>
                    {/* Arrow always accompanies the colour — direction never
                        rests on hue alone. */}
                    {dir !== 0 && (dir > 0
                      ? <ArrowDropUpIcon sx={{ fontSize: 14, color, ml: -0.5 }} />
                      : <ArrowDropDownIcon sx={{ fontSize: 14, color, ml: -0.5 }} />)}
                    <Typography sx={{ fontFamily: MONO, fontSize: bloomberg.fontSize.micro, color, whiteSpace: 'nowrap' }}>
                      {formatMacroChange(change, s.unit)}
                    </Typography>
                    <Box sx={{ ml: 'auto', display: { xs: 'none', lg: 'block' } }}>
                      {/* Muted, not status-coloured: the delta already carries
                          good/bad, and tinting both double-encodes it. */}
                      <MacroSparkline values={s.sparkline} width={52} height={16} />
                    </Box>
                  </Box>
                </Box>
              </MuiTooltip>
            );
          })}

      <ChevronRightIcon sx={{ fontSize: 16, color: bloomberg.text.muted, flexShrink: 0 }} />
    </Box>
  );
};

export default MacroRegimeStrip;
